import { useMemo, useCallback } from 'react';
import {
  getAllSegments,
  getClipFPS,
  getEndForSegment,
  getStartForSegment
} from '@goldcast/player-config/src/utils/clipUtils';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { Segment } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/Segment';
import { SegmentType } from '@goldcast/player-config/src/types/Segment/Type';
import { VideoSegment } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/VideoSegment';
import { CompositeSegment } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { CLIP_UPDATE_TYPE } from '../../clipUpdateDataUtilsWithSplits/constants';
import { updateClipDataWithSplits } from '../../clipUpdateDataUtilsWithSplits/updateWithSplits';
import { VIDEO_SEGMENT_MAX_FILE_SIZE } from '../../constants';
import { getPlayerTimeFromSegmentTime } from '../../timeUtils';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import useBrandKitComponent from '@/Pages/BrandKit/components/useBrandKitComponent';
import { getBrandKitUrl } from '@/Pages/BrandKit/components/utils';
import { showErrorToast } from '@/libs/toast/toast';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { useRemotionPlayerSeek } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerSeek';

const REMOVED_BRAND_KIT_NAME = 'placeholder';

/**
 * Player time at which `segment` begins on the timeline, or null if it cannot be resolved.
 *
 * COMPOSITE goes through getStartForSegment so the segment's own trim and deletes are accounted
 * for. VIDEO cannot: getStartForSegment resolves a segmentId via getCompositeSegmentById, which
 * throws for a non-composite id. A VIDEO segment plays start-to-end with no deletes, so its
 * timeline position is simply its own fromFrame.
 */
function getSegmentStartPlayerTime(clip: Clip, segment: Segment): number | null {
  if (segment.type === SegmentType.VIDEO) {
    const placed = getAllSegments(clip).find(seg => seg.id === segment.id);
    if (!placed) {
      return null;
    }
    return placed.fromFrame / getClipFPS(clip);
  }

  return getPlayerTimeFromSegmentTime(
    getStartForSegment({
      clip,
      segmentId: segment.id,
      isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
    }),
    clip,
    segment.id
  );
}

export function useAddSegmentUpload(previousSegment: Segment | null) {
  const { clipId, clipData } = useClipsContext();
  const { seekTo } = useRemotionPlayerSeek();
  const { brandKitStore, clipsBrandKitStore, onFilesUpload, deleteItem } = useBrandKitComponent({
    key: 'intro_outro',
    title: 'Video',
    clipId: clipData.id,
    maxFileSize: VIDEO_SEGMENT_MAX_FILE_SIZE
  });

  const brandKitIntroOutro = useMemo(() => {
    return brandKitStore.intro_outro.map(video => getBrandKitUrl(video));
  }, [brandKitStore.intro_outro]);

  const clipBrandKitIntroOutro = useMemo(() => {
    return clipsBrandKitStore[clipId]?.intro_outro;
  }, [clipsBrandKitStore, clipId]);

  const clipIntroOutro = useMemo(() => {
    function shouldAddCurrentIntroOutroToList(
      currentIntroOutro: string | undefined,
      introOutroPreviewList: { url: string; name: string }[]
    ): boolean {
      return (
        !!currentIntroOutro &&
        currentIntroOutro.includes('brand_kit') &&
        !brandKitIntroOutro.includes(currentIntroOutro) &&
        !introOutroPreviewList.find(video => video.url === currentIntroOutro)
      );
    }

    const introOutroPreviewList = clipBrandKitIntroOutro
      ? clipBrandKitIntroOutro?.map(video => ({
          name: video,
          url: getBrandKitUrl(video, clipData.id)
        }))
      : [];
    const currentIntro = clipData.asset_metadata.intro;
    if (shouldAddCurrentIntroOutroToList(currentIntro, introOutroPreviewList)) {
      introOutroPreviewList.push({ url: currentIntro!, name: REMOVED_BRAND_KIT_NAME });
    }
    const currentOutro = clipData.asset_metadata.outro;
    if (shouldAddCurrentIntroOutroToList(currentOutro, introOutroPreviewList)) {
      introOutroPreviewList.push({ url: currentOutro!, name: REMOVED_BRAND_KIT_NAME });
    }
    return introOutroPreviewList;
  }, [
    clipBrandKitIntroOutro,
    clipData.asset_metadata.intro,
    clipData.asset_metadata.outro,
    clipData.id,
    brandKitIntroOutro
  ]);

  function uploadFiles(files: File[]) {
    onFilesUpload(files).catch(() =>
      showErrorToast(
        `Oops! Uploading your video encountered an issue. Please make sure your file is in MP4 format and doesn't exceed ${VIDEO_SEGMENT_MAX_FILE_SIZE}MB, then give it another go.`
      )
    );
  }

  const handleVideoSelect = useCallback(
    (url: string, duration: number) => {
      let insertFrame = 0;
      if (previousSegment?.type === SegmentType.VIDEO) {
        const videoSegment = previousSegment as VideoSegment;
        insertFrame = videoSegment.currentBounds?.toFrame || videoSegment.maxBounds?.toFrame || 0;
      } else if (previousSegment?.type === SegmentType.COMPOSITE) {
        const compositeSegment = previousSegment as CompositeSegment;
        insertFrame = compositeSegment.currentBounds?.toFrame || compositeSegment.maxBounds?.toFrame || 0;
      }

      // No preceding segment means this gap is the start of the timeline, so the video is
      // prepended at frame 0 rather than anchored after something.
      const newClipData = updateClipDataWithSplits({
        clipId,
        actionType: CLIP_UPDATE_TYPE.ADD_SEGMENT_TRACK,
        type: SegmentType.VIDEO,
        id: null,
        url,
        duration,
        afterSegmentId: previousSegment?.id ?? null,
        insertFrame
      });

      if (!previousSegment) {
        seekTo(0);
        return;
      }

      const newPlayerTime = getPlayerTimeFromSegmentTime(
        getEndForSegment({
          clip: newClipData,
          segmentId: previousSegment.id,
          isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
        }),
        newClipData,
        previousSegment.id
      );
      seekTo(newPlayerTime);
    },
    [previousSegment, clipId, seekTo]
  );

  const handleClipSegmentInsert = useCallback(
    (segments: Segment[]) => {
      if (!segments.length) {
        return;
      }

      // No preceding segment means this gap is the start of the timeline, so the import is
      // prepended rather than anchored after something.
      const newClipData = updateClipDataWithSplits({
        clipId,
        actionType: CLIP_UPDATE_TYPE.ADD_CLIP_SEGMENTS,
        segments,
        afterSegmentId: previousSegment?.id ?? null
      });

      // Land the playhead at the start of the first imported segment so the user is looking at
      // what they just added and can play straight into it.
      const firstSegment = segments[0];
      const newPlayerTime = getSegmentStartPlayerTime(newClipData, firstSegment);
      if (newPlayerTime !== null) {
        seekTo(newPlayerTime);
      }
    },
    [previousSegment, clipId, seekTo]
  );

  return {
    clipIntroOutro,
    brandKitIntroOutro,
    brandKitStore,
    uploadFiles,
    onFilesUpload,
    handleVideoSelect,
    handleClipSegmentInsert,
    deleteItem,
    removedBrandKitName: REMOVED_BRAND_KIT_NAME
  };
}
